<template>
	<transition name="modal">
		<div v-if="open" class="fixed inset-0 z-[2000]" @keydown="onKeydown">
			<!-- Backdrop -->
			<div class="absolute inset-0 bg-black/50" />

			<!-- Layout wrapper -->
			<div
				class="relative flex items-center justify-center min-h-full p-4 text-neutral-900"
				@click.self="onBackdropClick"
			>
				<!-- Dialog -->
				<div
					ref="dialog"
					:class="dialogClasses"
					role="dialog"
					aria-modal="true"
					:aria-labelledby="labelledBy || (title ? titleId : null)"
					:aria-describedby="describedById"
					tabindex="-1"
					@click.stop
				>
					<!-- Header -->
					<slot name="header">
						<div
							v-if="!hideHeader"
							class="flex items-start justify-between gap-4 p-6 pb-4"
						>
							<div class="min-w-0">
								<h2
									v-if="title"
									:id="titleId"
									class="mb-0 font-sans text-xl font-semibold normal-case text-neutral-900"
								>
									{{ title }}
								</h2>
								<p
									v-if="subtitle"
									class="mt-1 text-sm text-neutral-600"
								>
									{{ subtitle }}
								</p>
							</div>

							<Button
								type="link"
								size="lg"
								class="px-2 h-7 text-neutral-800"
								aria-label="close"
								@click="requestClose('close-button')"
							>
								<Icon :icon="['far', 'times']" />
							</Button>
						</div>
					</slot>

					<!-- Body (scrollable) -->
					<div
						:id="describedById"
						:class="mergedBodyClasses"
						:style="bodyStyle"
					>
						<slot />
					</div>

					<!-- Footer -->
					<div
						v-if="$scopedSlots.footer"
						:class="mergedFooterClasses"
					>
						<slot
							name="footer"
							:close="() => requestClose('footer')"
							:primary-button-type="primaryButtonType"
							:primary-button-class="primaryButtonClass"
						/>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import { twMerge } from 'tailwind-merge'
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'

export default {
	name: 'ModalV2',
	components: {
		Button,
		Icon
	},
	props: {
		value: {
			type: Boolean,
			required: true
		},
		title: {
			type: String,
			default: null
		},
		subtitle: {
			type: String,
			default: null
		},
		variant: {
			type: String,
			default: 'default',
			validator: value =>
				['default', 'info', 'success', 'warning', 'danger'].includes(
					value
				)
		},
		size: {
			type: String,
			default: 'md' // sm | md | lg | xl
		},
		closeOnBackdrop: {
			type: Boolean,
			default: true
		},
		closeOnEsc: {
			type: Boolean,
			default: true
		},
		trapFocus: {
			type: Boolean,
			default: true
		},
		hideHeader: {
			type: Boolean,
			default: false
		},
		// id of an external element labelling the dialog, for when the caller
		// renders its own title (e.g. with hideHeader) instead of the `title` prop.
		labelledBy: {
			type: String,
			default: null
		},
		maxBodyVh: {
			type: Number,
			default: 80 // Modal body max height as % of viewport height
		},
		panelClass: {
			type: [String, Array, Object],
			default: ''
		},
		bodyClass: {
			type: [String, Array, Object],
			default: ''
		},
		footerClass: {
			type: [String, Array, Object],
			default: ''
		}
	},
	data() {
		return {
			titleId: `modalv2-title-${Math.random()
				.toString(36)
				.slice(2, 10)}`,
			describedById: `modalv2-desc-${Math.random()
				.toString(36)
				.slice(2, 10)}`,
			previousActiveElement: null,
			restoreBodyStyles: null
		}
	},
	computed: {
		open() {
			return this.value
		},
		dialogClasses() {
			return twMerge(
				'w-full bg-white rounded-lg shadow-2xl outline-none',
				this.sizeClasses,
				this.panelClass
			)
		},
		sizeClasses() {
			const map = {
				sm: 'max-w-md',
				md: 'max-w-2xl',
				lg: 'max-w-3xl',
				xl: 'max-w-4xl',
				xxl: 'max-w-5xl',
				xxxl: 'max-w-6xl'
			}

			return map[this.size] || map.md
		},
		mergedBodyClasses() {
			return twMerge('px-6 pb-6 overflow-y-auto', this.bodyClass)
		},
		mergedFooterClasses() {
			return twMerge(
				'flex flex-col-reverse gap-3 p-6 md:flex-row md:justify-end',
				this.footerClass
			)
		},
		primaryButtonType() {
			return 'primary'
		},
		primaryButtonClass() {
			const variants = {
				default: '',
				info:
					'bg-blue-700 hover:bg-blue-800 active:bg-blue-800 disabled:bg-blue-300 text-white',
				success:
					'bg-green-700 hover:bg-green-800 active:bg-green-800 disabled:bg-green-300 text-white',
				warning:
					'bg-amber-700 hover:bg-amber-800 active:bg-amber-800 disabled:bg-amber-300 text-white',
				danger:
					'bg-red-700 hover:bg-red-800 active:bg-red-800 disabled:bg-red-300 text-white'
			}

			return variants[this.variant] || variants.default
		},
		bodyStyle() {
			if (this.maxBodyVh <= 0) {
				return {}
			}

			return { maxHeight: `${this.maxBodyVh}vh` }
		}
	},
	watch: {
		open: {
			immediate: true,
			handler(isOpen) {
				if (isOpen) {
					this.onOpen()
				} else {
					this.onClose()
				}
			}
		}
	},
	methods: {
		requestClose(reason) {
			this.$emit('close', { reason })
			this.$emit('input', false)
		},
		onBackdropClick() {
			if (!this.closeOnBackdrop) {
				return
			}

			this.requestClose('backdrop')
		},
		onKeydown(event) {
			if (!this.open) {
				return
			}

			if (
				this.trapFocus &&
				event.key === 'Tab' &&
				!event.defaultPrevented
			) {
				this.trapTabKey(event)
			}

			if (this.closeOnEsc && event.key === 'Escape') {
				event.preventDefault()
				this.requestClose('esc')
				return
			}
		},
		onOpen() {
			this.previousActiveElement = document.activeElement

			this.lockBodyScroll()
			this.setupListeners()

			this.$nextTick(() => {
				const dialog = this.$refs.dialog

				if (!dialog) {
					return
				}

				dialog.focus?.()

				const focusables = this.getFocusable(dialog)

				if (focusables.length) {
					focusables[0].focus?.()
				}
			})
		},
		onClose() {
			this.teardownListeners()
			this.unlockBodyScroll()

			this.$nextTick(() => {
				this.previousActiveElement?.focus?.()
				this.previousActiveElement = null
			})
		},
		setupListeners() {
			window.addEventListener('keydown', this.onKeydown, true)
		},
		teardownListeners() {
			window.removeEventListener('keydown', this.onKeydown, true)
		},
		getFocusable(root) {
			const selector = [
				'a[href]',
				'button:not([disabled])',
				'input:not([disabled])',
				'select:not([disabled])',
				'textarea:not([disabled])',
				'[tabindex]:not([tabindex="-1"])'
			].join(',')

			return Array.from(root.querySelectorAll(selector)).filter(
				element => element.offsetParent !== null
			)
		},
		trapTabKey(event) {
			const dialog = this.$refs.dialog

			if (!dialog) {
				return
			}

			const focusables = this.getFocusable(dialog)

			if (!focusables.length) {
				event.preventDefault()
				dialog.focus?.()
				return
			}

			const first = focusables[0]
			const last = focusables[focusables.length - 1]
			const active = document.activeElement

			if (event.shiftKey) {
				if (active === first || active === dialog) {
					event.preventDefault()
					last.focus?.()
				}
			} else {
				if (active === last) {
					event.preventDefault()
					first.focus?.()
				}
			}
		},
		lockBodyScroll() {
			const body = document.body
			const documentElement = document.documentElement

			const prevOverflow = body.style.overflow
			const prevPaddingRight = body.style.paddingRight

			const scrollbarWidth =
				window.innerWidth - documentElement.clientWidth

			body.style.overflow = 'hidden'

			if (scrollbarWidth > 0) {
				body.style.paddingRight = `${scrollbarWidth}px`
			}

			this.restoreBodyStyles = () => {
				body.style.overflow = prevOverflow
				body.style.paddingRight = prevPaddingRight

				this.restoreBodyStyles = null
			}
		},
		unlockBodyScroll() {
			this.restoreBodyStyles?.()
		}
	},
	beforeDestroy() {
		this.teardownListeners()
		this.unlockBodyScroll()
	}
}
</script>
